import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { adminGetBookings, getConfig } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Printer, ArrowLeft } from "lucide-react";
import { parseISODate } from "@/lib/format";

const TOKEN_KEY = "ice_admin_token";
const SESSION_TIMES = ["09:30", "10:45", "12:00", "13:15", "14:15"];
const WEEKDAY_LONG = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const statusStyle = (s) => {
  const m = {
    submitted: "bg-slate-100 text-slate-900",
    "in-progress": "bg-amber-100 text-amber-900",
    confirmed: "bg-sky-500 text-white",
    "invoice sent": "bg-indigo-100 text-indigo-900",
    paid: "bg-emerald-500 text-white",
    cancelled: "bg-slate-300 text-slate-600 line-through",
  };
  return m[s] || m.submitted;
};

const humanDate = (iso) => {
  const d = parseISODate(iso);
  return `${WEEKDAY_LONG[(d.getDay() + 6) % 7]}, ${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
};

export default function AdminDaySummaryPage() {
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const [b, c] = await Promise.all([adminGetBookings(token), getConfig()]);
        setBookings(b);
        setConfig(c);
        if (c?.valid_dates?.length && !selectedDate) {
          // Default to today if in window, otherwise first valid date
          const todayIso = new Date().toISOString().slice(0, 10);
          setSelectedDate(c.valid_dates.includes(todayIso) ? todayIso : c.valid_dates[0]);
        }
      } catch {
        toast.error("Failed to load day summary");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const validDates = config?.valid_dates || [];
  const idx = selectedDate ? validDates.indexOf(selectedDate) : -1;
  const goPrev = () => idx > 0 && setSelectedDate(validDates[idx - 1]);
  const goNext = () => idx >= 0 && idx < validDates.length - 1 && setSelectedDate(validDates[idx + 1]);

  const daySessions = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (b.date !== selectedDate) continue;
      if (b.cancelled || b.status === "cancelled") continue;
      map[b.time] = b;
    }
    return SESSION_TIMES.map((t) => ({ time: t, booking: map[t] || null }));
  }, [bookings, selectedDate]);

  const totalPupils = daySessions.reduce((s, x) => s + (x.booking?.group_size || 0), 0);
  const bookedCount = daySessions.filter((x) => x.booking).length;

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <p className="font-mono-tight text-sm text-slate-600 mb-4">Admin sign-in required.</p>
          <Link to="/admin" className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 inline-block">
            Go to admin login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 print-clean">
      <div className="no-print"><Header /></div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {/* Toolbar */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6 no-print">
          <div>
            <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">
              Office view
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none">Day summary</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-3 py-2 flex items-center gap-2"
              data-testid="daysummary-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Admin
            </Link>
            <select
              value={selectedDate || ""}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-white border-2 border-slate-900 p-2 font-mono-tight text-sm"
              data-testid="daysummary-date-select"
            >
              {validDates.map((d) => (
                <option key={d} value={d}>{humanDate(d)}</option>
              ))}
            </select>
            <button onClick={goPrev} disabled={idx <= 0} className="bg-white border-2 border-slate-900 brutal-shadow brutal-hover p-2 disabled:opacity-40" data-testid="daysummary-prev">
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button onClick={goNext} disabled={idx < 0 || idx >= validDates.length - 1} className="bg-white border-2 border-slate-900 brutal-shadow brutal-hover p-2 disabled:opacity-40" data-testid="daysummary-next">
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={() => window.print()}
              className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
              data-testid="daysummary-print"
            >
              <Printer className="w-4 h-4" strokeWidth={2.5} /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Printable sheet */}
        <div className="bg-white border-2 border-slate-900 brutal-shadow print:border-0 print:shadow-none" data-testid="daysummary-sheet">
          {/* Sheet header */}
          <div className="p-6 md:p-8 border-b-2 border-slate-900 flex items-center justify-between print:p-4">
            <div>
              <div className="font-mono-tight text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">
                Sligo On Ice · Rink Booking 2026/27
              </div>
              <h2 className="font-display text-3xl md:text-4xl uppercase leading-none" data-testid="daysummary-title">
                {selectedDate ? humanDate(selectedDate) : "—"}
              </h2>
              <div className="mt-2 font-mono-tight text-xs text-slate-600">
                {bookedCount} of 4 sessions booked · {totalPupils} pupils total
              </div>
            </div>
            <div className="text-right font-mono-tight text-[10px] uppercase tracking-widest text-slate-500 print:hidden">
              Office copy
            </div>
          </div>

          {/* Sessions */}
          {loading ? (
            <div className="p-12 text-center font-mono-tight uppercase tracking-widest">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white font-mono-tight uppercase text-[11px] tracking-widest print:bg-slate-100 print:text-slate-900">
                <tr>
                  <th className="text-left px-4 py-3 w-24">Time</th>
                  <th className="text-left px-4 py-3">School</th>
                  <th className="text-left px-4 py-3">Teacher · contact</th>
                  <th className="text-left px-4 py-3 w-16">No.</th>
                  <th className="text-left px-4 py-3">Age group</th>
                  <th className="text-left px-4 py-3">Payment</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {daySessions.map(({ time, booking }) => (
                  <tr key={time} className="border-t-2 border-slate-900 align-top" data-testid={`daysummary-row-${time.replace(":","")}`}>
                    <td className="px-4 py-4 font-mono-tight font-bold text-lg">{time}</td>
                    {booking ? (
                      <>
                        <td className="px-4 py-4">
                          <div className="font-bold">{booking.school_name}</div>
                          <div className="text-slate-500 text-xs font-mono-tight">{booking.school_address}</div>
                          {booking.county && (
                            <div className="text-slate-500 text-xs font-mono-tight">{booking.county}</div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div>{booking.teacher_name}</div>
                          <div className="text-slate-600 text-xs font-mono-tight break-all">{booking.email}</div>
                          <div className="text-slate-600 text-xs font-mono-tight">{booking.phone}</div>
                        </td>
                        <td className="px-4 py-4 font-mono-tight font-bold text-xl">{booking.group_size}</td>
                        <td className="px-4 py-4 text-xs">{booking.age_group}</td>
                        <td className="px-4 py-4 text-xs">{booking.payment_method}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-block border-2 border-slate-900 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest ${statusStyle(booking.status)}`}>
                            {booking.status || "submitted"}
                          </span>
                        </td>
                      </>
                    ) : (
                      <td colSpan={6} className="px-4 py-4 font-mono-tight text-slate-400 uppercase tracking-widest text-xs italic">
                        — open · no booking —
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Sheet footer */}
          <div className="p-4 md:p-5 border-t-2 border-slate-900 bg-slate-50 flex items-center justify-between text-[10px] font-mono-tight uppercase tracking-widest text-slate-600">
            <span>€14 per child · Teachers &amp; SNAs skate FREE</span>
            <span>Generated {new Date().toLocaleString("en-IE")}</span>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-clean { background: #fff !important; }
          [data-testid="daysummary-sheet"] { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
